import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScoreGauge } from "../components/ScoreGauge";
import { CategoryBars } from "../components/CategoryBars";
import { usePublicConfig } from "../lib/useConfig";
import api, { track } from "../lib/api";
import {
  ArrowRight, Check, Lock, FileText, Compass, ClipboardList, Gauge, Sparkles,
  BookOpen, BarChart3, HandCoins, Users2, Building2, Quote,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1584365098838-50ccef838f4a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const PROBLEM_IMG = "https://images.unsplash.com/photo-1695388474402-ed805a890d8d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const WHO_IMG = "https://images.unsplash.com/photo-1655720357872-ce227e4164ba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const WHO_IMG2 = "https://images.unsplash.com/photo-1761370981247-1dfd749ec96b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const CTA_IMG = "https://images.unsplash.com/photo-1515657834497-26509e295154?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const SCENARIOS = [
  {
    icon: Building2,
    title: "A growing community nonprofit",
    body: "Strong programs and meaningful impact, but heavily dependent on a small number of grants — and unsure how to diversify revenue.",
  },
  {
    icon: Users2,
    title: "A small nonprofit ready to grow",
    body: "Strong programs and growing momentum, but fundraising has developed informally and the organization now needs clearer systems, stronger donor strategy, and a more intentional path to growth.",
  },
  {
    icon: Sparkles,
    title: "A volunteer-led nonprofit",
    body: "Plenty of ideas and community commitment, but limited staff capacity and no clear way to determine which funding opportunities are actually worth pursuing.",
  },
];

const CATEGORY_ICONS = { story: BookOpen, evidence: BarChart3, revenue: HandCoins, relationship: Users2, infrastructure: Building2 };

const Eyebrow = ({ children }) => (
  <span className="inline-block text-clay text-xs font-semibold tracking-[0.2em] uppercase mb-4">{children}</span>
);

export default function Landing() {
  const config = usePublicConfig();
  const [sample, setSample] = useState(null);
  useEffect(() => { track("landing_visit"); }, []);
  useEffect(() => { api.get("/sample-report").then((r) => setSample(r.data)).catch(() => {}); }, []);

  const problems = [
    "An unclear case for support", "Weak evidence of impact", "The wrong revenue strategy",
    "Limited donor relationships", "Inadequate systems", "Board disengagement",
    "Budget or governance concerns", "Programs that are hard to explain or measure",
  ];
  const steps = [
    { icon: ClipboardList, t: "Tell us about your organization", d: "Share your mission, programs, budget, and funding picture." },
    { icon: Compass, t: "Complete the assessment", d: "A guided 12–15 minute diagnostic across five readiness areas." },
    { icon: Gauge, t: "Receive your diagnostic score", d: "See your Funding Readiness Score and where you stand." },
    { icon: Sparkles, t: "Unlock your Blueprint", d: "Get a detailed report and a prioritized action plan." },
  ];
  const receives = [
    "Overall Funding Readiness Score", "Score for each assessment category", "Top organizational strengths",
    "Priority funding gaps", "Recommended funding pathways", "Revenue strategies to deprioritize",
    "A clear funding narrative", "Missing evidence and documentation", "Immediate action steps",
    "A 30-, 60-, and 90-day improvement plan", "Leadership and board discussion questions",
  ];
  const audiences = [
    "Small & midsize nonprofits", "New nonprofit founders", "Community-based organizations",
    "Early-stage & growing nonprofits", "Churches & faith-based organizations", "Volunteer-led organizations",
    "Social-impact organizations", "Organizations preparing for grants", "Groups diversifying revenue",
    "Teams without a full-time development staff",
  ];
  const sampleScore = sample?.overall_score ?? 66;

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-cream overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 grid lg:grid-cols-2 gap-14 items-center">
          <div className="animate-fade-up">
            <Eyebrow>Fundraising readiness for nonprofits</Eyebrow>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-ink">
              Is Your Organization Ready to Raise More Money?
            </h1>
            <p className="mt-6 text-ink/70 text-lg leading-relaxed max-w-xl">
              FundReady Lab helps nonprofits and mission-driven organizations see themselves the way a funder or donor
              might. Find out what's working, where the gaps are, and what to strengthen before your next ask.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/assessment/new" data-testid="hero-start-assessment"
                className="btn-gold font-semibold px-8 py-4 rounded-[3px] flex items-center justify-center gap-2 text-base">
                Check Your Funding Readiness <ArrowRight size={18} />
              </Link>
              <Link to="/how-it-works" data-testid="hero-how-it-works"
                className="border border-ink/20 text-ink font-semibold px-8 py-4 rounded-[3px] hover:border-clay hover:text-clay transition-colors text-center">
                See What You'll Receive
              </Link>
            </div>
            <p className="mt-6 text-muted-foreground text-sm">Get your free snapshot in minutes. No credit card required.</p>
            {sample?.id && (
              <Link to={`/report/${sample.id}?sample=1`} data-testid="hero-sample-report"
                className="mt-3 inline-flex items-center gap-1.5 text-clay font-semibold text-sm hover:gap-2.5 transition-all">
                Explore a full sample report <ArrowRight size={15} />
              </Link>
            )}
          </div>

          <div className="animate-fade-up relative" style={{ animationDelay: "0.15s" }}>
            <div className="rounded-md overflow-hidden border border-line">
              <img src={HERO_IMG} alt="A small group of nonprofit leaders talking through their plans together" className="w-full h-[420px] object-cover" />
            </div>
            {/* floating sample score card */}
            <div className="absolute -bottom-8 -left-4 sm:left-6 w-64 bg-offwhite rounded-md border border-line shadow-lg p-5">
              <p className="text-[11px] tracking-widest uppercase text-muted-foreground">Sample snapshot</p>
              <div className="mt-1 flex items-end gap-2">
                <span className="font-display text-4xl font-extrabold text-green">{sampleScore}</span>
                <span className="text-muted-foreground text-sm mb-1">/ 100</span>
              </div>
              <p className="text-sm text-ink/70 mt-1">{sample?.org_name || "Bridgewater Youth Collective"}</p>
              <div className="mt-3 h-1.5 bg-line rounded-full overflow-hidden"><div className="h-full bg-ochre rounded-full" style={{ width: `${sampleScore}%` }} /></div>
              {sample?.id && <Link to={`/report/${sample.id}?sample=1`} className="mt-3 block text-clay text-xs font-semibold hover:underline">View the full sample report →</Link>}
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <Eyebrow>Start here</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold text-ink leading-tight">You may not need another grant application.</h2>
            <p className="mt-5 text-lg text-ink/70 leading-relaxed">
              If fundraising has stalled, the problem may not be a lack of opportunities. Your story may be too broad.
              Your results may be hard to explain. Your board may not be opening doors. Or grants may simply not be your
              best next move. We help you figure out what to address first.
            </p>
            <div className="mt-8 p-7 bg-green text-cream rounded-md relative grain overflow-hidden">
              <Quote className="text-ochre mb-3" size={24} />
              <p className="relative z-10 text-cream/95 leading-relaxed font-serif-accent text-lg">
                Before you write another proposal or send another appeal, find out what's actually standing between your
                organization and the money it needs.
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-px bg-line border border-line rounded-md overflow-hidden">
            {problems.map((p) => (
              <div key={p} className="bg-ivory p-5 hover-lift">
                <div className="w-8 h-8 rounded-[3px] bg-copper/10 flex items-center justify-center mb-3">
                  <span className="w-1.5 h-1.5 bg-copper rotate-45" />
                </div>
                <p className="text-sm text-charcoal font-medium leading-snug">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white border-y border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="max-w-2xl">
            <Eyebrow>How It Works</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold text-navy">A clear path from uncertainty to a plan</h2>
          </div>
          <div className="mt-14 grid md:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <div key={s.t} className="relative p-7 bg-ivory border border-line rounded-md hover-lift">
                <span className="font-display text-5xl font-extrabold text-line absolute top-4 right-5">{i + 1}</span>
                <s.icon className="text-gold mb-5" size={26} />
                <h3 className="font-display font-semibold text-navy text-lg leading-snug">{s.t}</h3>
                <p className="mt-2.5 text-sm text-charcoal/70 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="max-w-2xl mb-14">
          <Eyebrow>What We Assess</Eyebrow>
          <h2 className="text-3xl sm:text-4xl font-bold text-navy">Five categories of funding readiness</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 border-t border-l border-line rounded-md overflow-hidden grid-lines">
          {(config?.categories || []).map((c, i) => {
            const Icon = CATEGORY_ICONS[c.key] || BookOpen;
            return (
              <div key={c.key} className="bg-ivory p-7 hover-lift" style={{ animationDelay: `${i * 60}ms` }}>
                <Icon className="text-navy mb-5" size={26} />
                <div className="text-gold text-xs font-semibold mb-1">0{i + 1}</div>
                <h3 className="font-display font-semibold text-navy text-lg mb-2">{c.name}</h3>
                <p className="text-sm text-charcoal/70 leading-relaxed">{c.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* WHAT YOU RECEIVE */}
      <section className="bg-green text-cream relative grain overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-2 gap-14">
          <div>
            <span className="inline-block text-ochre text-xs font-semibold tracking-[0.2em] uppercase mb-4">The full report</span>
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight">What's inside your report</h2>
            <p className="mt-5 text-cream/80 leading-relaxed">
              Your score tells you where you stand. The full report tells you what to do about it — honest, specific to
              your answers, and written like advice from someone who's done this before.
            </p>
            <Link to="/pricing" className="mt-8 inline-flex items-center gap-2 text-ochre font-semibold hover:gap-3 transition-all">
              See what it costs <ArrowRight size={18} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {receives.map((r) => (
              <div key={r} className="flex items-start gap-3 p-4 bg-cream/5 border border-cream/15 rounded-[3px]">
                <Check size={16} className="text-ochre mt-0.5 shrink-0" />
                <span className="text-sm text-cream/90">{r}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-2 gap-14 items-center">
        <div className="grid grid-cols-5 grid-rows-6 gap-3 h-[440px]">
          <div className="col-span-3 row-span-6 rounded-md overflow-hidden border border-line">
            <img src={WHO_IMG} alt="Community leaders talking through their work around a table" className="w-full h-full object-cover" />
          </div>
          <div className="col-span-2 row-span-3 rounded-md overflow-hidden border border-line">
            <img src={WHO_IMG2} alt="Two nonprofit leaders working together" className="w-full h-full object-cover" />
          </div>
          <div className="col-span-2 row-span-3 rounded-md bg-green flex items-center justify-center p-5">
            <p className="text-cream font-serif-accent text-lg leading-snug">Real organizations. Different stages. One honest read.</p>
          </div>
        </div>
        <div>
          <Eyebrow>Who it's for</Eyebrow>
          <h2 className="text-3xl sm:text-4xl font-bold text-ink">Made for nonprofits like yours</h2>
          <p className="mt-5 text-ink/70 leading-relaxed">
            New or long-running, big budget or all-volunteer — the questions are the same ones a good fundraiser would ask.
            The advice changes to fit where you actually are.
          </p>
          <div className="mt-8 flex flex-wrap gap-2.5">
            {audiences.map((a) => (
              <span key={a} className="px-4 py-2 bg-offwhite border border-line rounded-full text-sm text-ink/75">{a}</span>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT'S BUILT FOR — transparent scenarios (no testimonials until real ones exist) */}
      <section className="bg-sand/50 border-y border-line" data-testid="scenarios-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Eyebrow>Who it's built for</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold text-ink">Built for organizations facing real fundraising questions</h2>
            <p className="mt-4 text-ink/70">Fundraising challenges often look different on the surface, but the underlying questions are remarkably similar: Where should we focus? What's holding us back? And what should we strengthen before making the next ask?</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {SCENARIOS.map((s, i) => (
              <div key={s.title} data-testid={`scenario-${i}`}
                className="p-7 bg-white border border-line rounded-md flex flex-col hover-lift">
                <div className="w-11 h-11 rounded-[3px] bg-green/10 flex items-center justify-center mb-5">
                  <s.icon className="text-green" size={22} />
                </div>
                <h3 className="font-display font-semibold text-ink text-lg leading-snug">{s.title}</h3>
                <p className="mt-3 text-ink/75 leading-relaxed text-sm flex-1">{s.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-xs text-muted-foreground">Illustrative situations — not customer testimonials — to help you see where you might fit. We'll share real beta-user stories here once they're in.</p>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="bg-offwhite border-y border-line">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Eyebrow>Pricing</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold text-ink">Start free. Pay only when it's useful.</h2>
            <p className="mt-4 text-ink/70">Three ways to go, so you only pay for the depth you need.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            <div className="p-7 bg-ivory border border-line rounded-md flex flex-col">
              <h3 className="font-display font-bold text-navy text-lg">Free Funding Snapshot</h3>
              <div className="mt-2 font-display text-3xl font-extrabold text-navy">$0</div>
              <ul className="mt-5 space-y-2.5 text-sm text-charcoal/80 flex-1">
                {["Shortened assessment", "Preliminary overall score", "One strength, gap & next step"].map((x) => (
                  <li key={x} className="flex gap-2.5"><Check size={16} className="text-forest mt-0.5 shrink-0" />{x}</li>
                ))}
              </ul>
              <Link to="/assessment/new" data-testid="pricing-free-cta"
                className="mt-7 block text-center border border-ink text-ink font-semibold px-6 py-3 rounded-[3px] hover:bg-ink hover:text-cream transition-colors">
                Get Your Free Snapshot
              </Link>
            </div>
            <div className="p-7 bg-cream border-2 border-ochre/60 rounded-md flex flex-col relative">
              <span className="absolute -top-3 left-7 text-[11px] bg-clay text-cream font-bold px-3 py-1 rounded-full">Most Popular</span>
              <h3 className="font-display font-bold text-ink text-lg">Focus Report</h3>
              <div className="mt-2 font-display text-3xl font-extrabold text-ink">${config?.pricing?.tiers?.focus?.prices?.usd ?? 29}</div>
              <ul className="mt-5 space-y-2.5 text-sm text-ink/80 flex-1">
                {["Full assessment", "All 5 category scores", "Strengths & areas to strengthen", "Recommended funding pathways"].map((x) => (
                  <li key={x} className="flex gap-2.5"><Check size={16} className="text-clay mt-0.5 shrink-0" />{x}</li>
                ))}
              </ul>
              <Link to="/assessment/new" data-testid="pricing-focus-cta"
                className="mt-7 block text-center bg-clay text-cream font-semibold px-6 py-3 rounded-[3px] hover:bg-clay/90 transition-colors">
                Start with Focus
              </Link>
            </div>
            <div className="p-7 bg-green text-cream rounded-md relative grain overflow-hidden flex flex-col">
              <span className="absolute top-5 right-5 z-10 text-xs bg-ochre/90 text-ink font-semibold px-3 py-1 rounded-full">Full Strategy</span>
              <div className="relative z-10 flex flex-col flex-1">
                <h3 className="font-display font-bold text-lg">Full Blueprint</h3>
                <div className="mt-2 font-display text-3xl font-extrabold">${config?.pricing?.tiers?.full?.launch_prices?.usd ?? 79}</div>
                <ul className="mt-5 space-y-2.5 text-sm text-cream/90 flex-1">
                  {["Everything in Focus", "Detailed written analysis", "Funding narrative & 30/60/90 plan", "Downloadable PDF + email delivery"].map((x) => (
                    <li key={x} className="flex gap-2.5"><Check size={16} className="text-ochre mt-0.5 shrink-0" />{x}</li>
                  ))}
                </ul>
                <Link to="/assessment/new" data-testid="pricing-full-cta"
                  className="mt-7 block text-center btn-gold font-semibold px-6 py-3 rounded-[3px]">
                  Get the Full Report
                </Link>
              </div>
            </div>
          </div>
          <div className="mt-8 text-center">
            <Link to="/pricing" data-testid="landing-compare-plans" className="inline-flex items-center gap-2 text-clay font-semibold hover:gap-3 transition-all">
              Compare all plans (and see local currency pricing) <ArrowRight size={16} />
            </Link>
          </div>
          <div className="mt-10 flex items-start gap-3 max-w-3xl mx-auto p-5 bg-ivory border border-line rounded-md">
            <Lock size={18} className="text-copper mt-0.5 shrink-0" />
            <p className="text-sm text-muted-foreground leading-relaxed">
              {config?.disclaimer_short}
            </p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden">
        <img src={CTA_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" />
        <div className="absolute inset-0 bg-green/90" />
        <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-8 py-24 text-center text-cream">
          <h2 className="text-3xl sm:text-4xl font-bold">Your score tells you where you stand.</h2>
          <p className="mt-3 text-xl text-ochre font-serif-accent">The full report shows you what to do next.</p>
          <Link to="/assessment/new" data-testid="final-cta"
            className="mt-9 inline-flex items-center gap-2 btn-gold font-semibold px-8 py-4 rounded-[3px] text-base">
            See Where You Stand <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
